import { useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { SubscriptionService } from '../lib/subscriptionService';

export const useUserProfile = () => {
  const { user, isLoaded } = useUser();
  const [isProfileReady, setIsProfileReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!isLoaded || !user) {
      setIsProfileReady(false);
      return;
    }

    const ensureUserProfile = async () => {
      try {
        setError(null);
        console.log('Ensuring user profile for:', user.id);
        
        // Check if user profile exists
        let userProfile = await SubscriptionService.getUserProfile(user.id);
        
        // If not, create one
        if (!userProfile) {
          console.log('Creating user profile for new user');
          userProfile = await SubscriptionService.ensureUserProfile(
            user.id,
            user.emailAddresses[0]?.emailAddress || 'user@example.com',
            user.firstName || undefined,
            user.lastName || undefined
          );
        }
        
        console.log('User profile ready:', userProfile?.id);
        setIsProfileReady(true);
        setError(null);
        setRetryCount(0);
      } catch (err) {
        console.error('Error ensuring user profile:', err);
        
        // If it's a 406/409 error and we haven't retried too many times, try again
        if ((err instanceof Error && (err.message.includes('406') || err.message.includes('409'))) && retryCount < 3) {
          console.log(`Retrying profile setup (attempt ${retryCount + 1}/3)...`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            ensureUserProfile();
          }, 2000 * (retryCount + 1)); // Exponential backoff
          return;
        }
        
        setError('Failed to initialize user profile. Please refresh the page or contact support.');
        setIsProfileReady(false);
      }
    };

    ensureUserProfile();
  }, [isLoaded, user, retryCount]);

  return {
    isProfileReady,
    error,
    user,
    retryCount
  };
}; 